import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Star, X } from "lucide-react";



/* ---------- Star Catching ---------- */
export const StarCatchingGame = ({ childId, onClose, onReward }) => {
    const [score, setScore] = useState(0);
    const [stars, setStars] = useState([]);
    const [timeLeft, setTimeLeft] = useState(20);
    const [paused, setPaused] = useState(false);


    useEffect(() => {
        if (paused || timeLeft <= 0) return;


        const spawn = setInterval(() => {
            setStars((s) => [
                ...s,
                {
                    id: Math.random().toString(36).slice(2),
                    left: Math.random() * 88,
                    duration: 2.5 + Math.random() * 1.5,
                },
            ]);
        }, 600);


        const tick = setInterval(() => {
            setTimeLeft((t) => Math.max(0, t - 1));
        }, 1000);


        return () => {
            clearInterval(spawn);
            clearInterval(tick);
        };
    }, [paused, timeLeft]);


    useEffect(() => {
        if (timeLeft !== 0) return;


        const previousHighScore =
            Number(localStorage.getItem(`starHighScore_${childId}`)) || 0;


        if (score > previousHighScore) {
            localStorage.setItem(`starHighScore_${childId}`, score);
            toast.success(`✨ New High Score: ${score}`);
        } else {
            toast.success(`Score: ${score}`);
        }


        onReward?.({ arcade_score: score });
    }, [timeLeft, childId, score, onReward]);


    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-md flex items-center justify-center" data-testid="star-game-modal">
            <div className="bb-glass rounded-3xl p-6 w-[min(92vw,520px)] h-[min(80vh,640px)] relative overflow-hidden">
                <header className="flex items-center justify-between mb-3 relative z-10">
                    <div>
                        <h3 className="font-display text-2xl">Star Catching</h3>
                        <p className="text-sm text-[#b0b8d6]">
                            Score: <b className="text-[#ffd166]">{score}</b> · Time: {timeLeft}s
                            {paused ? " · Paused" : ""}
                        </p>
                    </div>


                    <div className="flex gap-2">
                        <button
                            onClick={() => setPaused((p) => !p)}
                            className="bb-btn-ghost !p-2"
                        >
                            {paused ? "Resume" : "Pause"}
                        </button>


                        <button onClick={onClose} className="bb-btn-ghost !p-2" data-testid="star-game-close">
                            <X size={18} />
                        </button>
                    </div>
                </header>


                <div className="absolute inset-0 mt-20 overflow-hidden">
                    {paused && timeLeft > 0 && (
                        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40">
                            <p className="font-display text-3xl text-[#ffd166]">Paused</p>
                        </div>
                    )}


                    {timeLeft > 0 &&
                        stars.map((s) => (
                            <button
                                key={s.id}
                                disabled={paused}
                                onClick={() => {
                                    if (paused) return;


                                    setScore((x) => x + 1);
                                    setStars((arr) => arr.filter((y) => y.id !== s.id));
                                }}
                                className="absolute disabled:pointer-events-none"
                                style={{
                                    left: `${s.left}%`,
                                    top: 0,
                                    animation: paused
                                        ? "none"
                                        : `bb-star-fall ${s.duration}s linear forwards`,
                                }}
                                data-testid="falling-star"
                            >
                                <Star size={32} fill="#ffd166" className="text-yellow-300 drop-shadow-[0_0_12px_rgba(255,209,102,0.8)]" />
                            </button>
                        ))}


                    {timeLeft === 0 && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-8">
                            <Sparkles className="text-[#ffd166]" size={42} />
                            <h4 className="font-display text-3xl">You caught {score} stars!</h4>
                            <p className="text-[#b0b8d6]">Awesome! Arcade score saved.</p>
                            <button onClick={onClose} className="bb-btn-primary mt-2" data-testid="star-game-done">
                                Continue
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ---------- Moonlight Memory ---------- */
const MEMORY_ICONS = [
    "🫐", "🌙", "⭐", "🍄", "✨", "🌟", "🦉", "🐈‍⬛",
    "🪄", "🔮", "🎩", "🪶", "🌌", "⚡", "🌸", "💎",
    "🐭", "🦋", "🍵", "🎀", "🪽", "👑", "🌷", "🍯",
    "🦊", "🐢", "🌺", "🦄", "🐉", "🌈", "🎈", "🍒",
    "🌼", "🍀", "🪷", "🌜", "🌞", "🦔", "🐝", "🦚",
    "🌹", "🌳", "🌵", "🪴", "🐿️", "🦝", "🐰", "🦌",
];


const DIFFICULTIES = [
    { key: "easy", label: "Easy", pairs: 4 },
    { key: "medium", label: "Medium", pairs: 8 },
    { key: "hard", label: "Hard", pairs: 18 },
    { key: "insane", label: "Insane", pairs: 50 },
];


const buildDeck = (pairs) => {
    const pool = [];


    for (let i = 0; i < pairs; i++) {
        pool.push(MEMORY_ICONS[i % MEMORY_ICONS.length] + (i < MEMORY_ICONS.length ? "" : "·"));
    }


    return [...pool, ...pool]
        .map((icon, i) => ({ id: i, icon, flipped: false, matched: false }))
        .sort(() => Math.random() - 0.5);
};


export const MemoryGame = ({ childId, onClose, onReward, onCoinsSpent }) => {
    const ENTRY_COSTS = {
        easy: 5,
        medium: 10,
        hard: 15,
        insane: 20,
    };


    const [diff, setDiff] = useState("easy");
    const [chargedDiff, setChargedDiff] = useState(null);
    const pairs = DIFFICULTIES.find((d) => d.key === diff)?.pairs ?? 4;


    const [cards, setCards] = useState(() => buildDeck(pairs));
    const [picked, setPicked] = useState([]);
    const [done, setDone] = useState(false);
    const [paused, setPaused] = useState(false);


    const chooseDiff = (d) => {
        const cost = ENTRY_COSTS[d.key];


        const currentCoins = Number(
            localStorage.getItem("shadowGroveArcadeCoins") || 0
        );


        if (currentCoins < cost) {
            alert(`You need ${cost} Arcade Coins to play ${d.label}.`);
            return;
        }


        localStorage.setItem(
            "shadowGroveArcadeCoins",
            String(currentCoins - cost)
        );


        onCoinsSpent?.(cost);


        setDiff(d.key);
        setCards(buildDeck(d.pairs));
        setPicked([]);
        setDone(false);
        setPaused(false);
        setChargedDiff(d.key);
    };


    const flip = (i) => {
        if (paused) return;
        if (done) return;


        if (!chargedDiff) {
            toast.error("Choose a Memory level first ✨");
            return;
        }


        if (cards[i].flipped || cards[i].matched || picked.length === 2) return;


        const next = cards.map((c, idx) =>
            idx === i ? { ...c, flipped: true } : c
        );


        setCards(next);


        const newPicked = [...picked, i];
        setPicked(newPicked);


        if (newPicked.length === 2) {
            setTimeout(() => {
                const [a, b] = newPicked;


                if (next[a].icon === next[b].icon) {
                    const matched = next.map((c, idx) =>
                        idx === a || idx === b ? { ...c, matched: true } : c
                    );


                    setCards(matched);


                    if (matched.every((c) => c.matched)) {
                        setDone(true);
                    }
                } else {
                    setCards(
                        next.map((c, idx) =>
                            idx === a || idx === b ? { ...c, flipped: false } : c
                        )
                    );
                }


                setPicked([]);
            }, 700);
        }
    };


    useEffect(() => {
        if (!done) return;


        (async () => {
            try {
                const r = await api.post(`/children/${childId}/interact`, {
                    kind: "memory_game",
                });


                onReward?.(r.data);
            } catch {}
        })();
    }, [done, childId, onReward]);


    const cols = pairs <= 4 ? 4 : pairs <= 8 ? 4 : pairs <= 18 ? 6 : 10;


    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-md flex items-center justify-center p-2" data-testid="memory-game-modal">
            <div className="bb-glass rounded-3xl p-4 w-[min(96vw,800px)] max-h-[95vh] overflow-auto relative">
                <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <h3 className="font-display text-2xl">Moonlight Memory</h3>
                        <p className="text-sm text-[#b0b8d6]">
                            Choose a level to start. Higher levels cost more.
                        </p>
                    </div>


                    <div className="flex flex-wrap gap-1">
                        {DIFFICULTIES.map((d) => (
                            <button
                                key={d.key}
                                disabled={paused}
                                onClick={() => chooseDiff(d)}
                                className={`px-3 py-1 rounded-full text-xs font-bold ${
                                    diff === d.key
                                        ? "bg-[#3a86ff] text-white"
                                        : "bg-white/5 text-[#b0b8d6]"
                                }`}
                            >
                                {d.label} · {ENTRY_COSTS[d.key]} coins
                            </button>
                        ))}


                        <button
                            onClick={() => setPaused((p) => !p)}
                            className="bb-btn-ghost !p-2 ml-2"
                            disabled={!chargedDiff}
                        >
                            {paused ? "Resume" : "Pause"}
                        </button>


                        <button onClick={onClose} className="bb-btn-ghost !p-2" data-testid="memory-game-close">
                            <X size={18} />
                        </button>
                    </div>
                </header>


                {!chargedDiff && (
                    <div className="mb-3 rounded-xl bg-black/30 border border-[#ffd166]/30 p-3 text-center text-[#ffd166] font-display text-xl">
                        Pick a level to begin
                    </div>
                )}


                {paused && (
                    <div className="mb-3 rounded-xl bg-black/30 border border-[#ffd166]/30 p-3 text-center text-[#ffd166] font-display text-xl">
                        Paused
                    </div>
                )}


                <div
                    className="grid gap-1.5"
                    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                >
                    {cards.map((c, i) => (
                        <button
                            key={c.id}
                            onClick={() => flip(i)}
                            disabled={paused || c.flipped || c.matched || done}
                            className={`aspect-square rounded-xl flex items-center justify-center transition-all ${
                                c.flipped || c.matched
                                    ? "bg-[#3a86ff]/20 border-2 border-[#7cc1ff]"
                                    : "bg-[#12182b] border border-[#1f2942] hover:border-[#3a86ff]"
                            }`}
                            style={{ fontSize: cols >= 8 ? 16 : 28 }}
                            data-testid={`memory-card-${i}`}
                        >
                            {c.flipped || c.matched ? c.icon : "?"}
                        </button>
                    ))}
                </div>


                {done && (
                    <div className="mt-4 text-center">
                        <p className="text-[#ffd166] font-semibold">All pairs found! +5 Points</p>
                        <button onClick={onClose} className="bb-btn-primary mt-3" data-testid="memory-game-done">
                            Continue
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};


/* ---------- Star Mine: Minesweeper Style ---------- */
export const StarMineGame = ({ childId, onClose, onReward, onCoinsSpent }) => {
    const LEVELS = [
        { key: "easy", label: "Easy", size: 4, mines: 3 },
        { key: "medium", label: "Medium", size: 5, mines: 5 },
        { key: "hard", label: "Hard", size: 6, mines: 8 },
        { key: "insane", label: "Insane", size: 8, mines: 14 },
    ];


    const ENTRY_COSTS = {
        easy: 5,
        medium: 10,
        hard: 15,
        insane: 20,
    };


    const [level, setLevel] = useState("easy");
    const [chargedLevel, setChargedLevel] = useState(null);
    const settings = LEVELS.find((l) => l.key === level) || LEVELS[0];


    const makeBoard = (boardSettings = settings) => {
        const total = boardSettings.size * boardSettings.size;
        const minePositions = new Set();


        while (minePositions.size < boardSettings.mines) {
            minePositions.add(Math.floor(Math.random() * total));
        }


        return Array.from({ length: total }, (_, i) => {
            const row = Math.floor(i / boardSettings.size);
            const col = i % boardSettings.size;


            const neighbors = [
                [row - 1, col - 1],
                [row - 1, col],
                [row - 1, col + 1],
                [row, col - 1],
                [row, col + 1],
                [row + 1, col - 1],
                [row + 1, col],
                [row + 1, col + 1],
            ];


            const nearbyMines = neighbors.filter(([r, c]) => {
                if (r < 0 || r >= boardSettings.size || c < 0 || c >= boardSettings.size) return false;
                return minePositions.has(r * boardSettings.size + c);
            }).length;


            return {
                id: i,
                mine: minePositions.has(i),
                nearbyMines,
                revealed: false,
                flagged: false,
            };
        });
    };


    const [tiles, setTiles] = useState(() => makeBoard());
    const [done, setDone] = useState(false);
    const [lost, setLost] = useState(false);


    const chooseLevel = (l) => {
        const cost = ENTRY_COSTS[l.key];


        const currentCoins = Number(
            localStorage.getItem("shadowGroveArcadeCoins") || 0
        );


        if (currentCoins < cost) {
            alert(`You need ${cost} Arcade Coins to play ${l.label}.`);
            return;
        }


        localStorage.setItem(
            "shadowGroveArcadeCoins",
            String(currentCoins - cost)
        );


        onCoinsSpent?.(cost);


        setLevel(l.key);
        setTiles(makeBoard(l));
        setDone(false);
        setLost(false);
        setChargedLevel(l.key);
    };


    const revealTile = (id) => {
        if (done) return;


        if (!chargedLevel) {
            toast.error("Choose a Star Mine level first ✨");
            return;
        }


        const tile = tiles.find((t) => t.id === id);
        if (!tile || tile.revealed || tile.flagged) return;


        if (tile.mine) {
            setTiles((prev) => prev.map((t) => ({ ...t, revealed: true })));
            setLost(true);
            setDone(true);
            toast.error("Supernova! Game Over");
            return;
        }


        const next = tiles.map((t) =>
            t.id === id ? { ...t, revealed: true } : t
        );


        const safeTiles = next.filter((t) => !t.mine);
        const revealedSafeTiles = safeTiles.filter((t) => t.revealed);


        if (revealedSafeTiles.length === safeTiles.length) {
            setDone(true);
            toast.success("You cleared the Star Mine ✨");
            onReward?.({ arcade_score: safeTiles.length });
        }


        setTiles(next);
    };


    const toggleFlag = (e, id) => {
        e.preventDefault();
        if (done) return;


        setTiles((prev) =>
            prev.map((t) =>
                t.id === id && !t.revealed ? { ...t, flagged: !t.flagged } : t
            )
        );
    };


    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-md flex items-center justify-center p-2">
            <div className="bb-glass rounded-3xl p-5 w-[min(96vw,720px)] max-h-[95vh] overflow-auto">
                <header className="flex items-center justify-between mb-4 gap-3">
                    <div>
                        <h3 className="font-display text-2xl">Star Mine</h3>
                        <p className="text-sm text-[#b0b8d6]">
                            Choose a level, reveal safe stars, and avoid cursed stars.
                        </p>
                    </div>


                    <button onClick={onClose} className="bb-btn-ghost !p-2">
                        <X size={18} />
                    </button>
                </header>


                <div className="flex flex-wrap gap-2 mb-4">
                    {LEVELS.map((l) => (
                        <button
                            key={l.key}
                            onClick={() => chooseLevel(l)}
                            className={`px-3 py-1 rounded-full text-xs font-bold ${
                                level === l.key
                                    ? "bg-[#3a86ff] text-white"
                                    : "bg-white/5 text-[#b0b8d6]"
                            }`}
                        >
                            {l.label} · {ENTRY_COSTS[l.key]} coins
                        </button>
                    ))}
                </div>


                {!chargedLevel && (
                    <div className="mb-3 rounded-xl bg-black/30 border border-[#ffd166]/30 p-3 text-center text-[#ffd166] font-display text-xl">
                        Pick a level to begin
                    </div>
                )}


                <div
                    className="grid gap-2 mx-auto"
                    style={{
                        gridTemplateColumns: `repeat(${settings.size}, minmax(0, 1fr))`,
                        maxWidth: settings.size >= 8 ? 560 : 440,
                    }}
                >
                    {tiles.map((tile) => (
                        <button
                            key={tile.id}
                            onClick={() => revealTile(tile.id)}
                            onContextMenu={(e) => toggleFlag(e, tile.id)}
                            disabled={tile.revealed || done}
                            className={`aspect-square rounded-xl border flex items-center justify-center font-bold ${
                                tile.revealed
                                    ? tile.mine
                                        ? "bg-red-500/20 border-red-400 text-red-300"
                                        : "bg-[#ffd166]/15 border-[#ffd166]/50 text-[#ffd166]"
                                    : "bg-[#12182b] border-[#1f2942] hover:border-[#3a86ff] text-white"
                            }`}
                        >
                            {tile.revealed
                                ? tile.mine
                                    ? "💥"
                                    : tile.nearbyMines > 0
                                    ? tile.nearbyMines
                                    : "✨"
                                : tile.flagged
                                ? "🚩"
                                : "⭐"}
                        </button>
                    ))}
                </div>


                {done && (
                    <div className="mt-5 text-center">
                        <p className="text-[#ffd166] font-semibold">
                            {lost
                                ? "Supernova! Game Over."
                                : "You cleared the Star Mine!"}
                        </p>


                        <button
                            onClick={() => chooseLevel(settings)}
                            className="bb-btn-primary mt-3"
                        >
                            Play Again
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};


